import { Box, MenuItem, Stack, Typography } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import { deepOrange, deepPurple } from "@mui/material/colors";
import moment from "moment";
import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { StatusRenderer } from "../../../components/ActiveInActiveStatus/ActiveInActiveStatus";
import { StandardCard } from "../../../components/Cards";
import { PageContainer } from "../../../components/container/PageContainer";
import { CustomLink } from "../../../components/CustomLink/CustomLink";
import LoadingDialog from "../../../components/Dialogs/LoadingDialog";
import { CustomSelect } from "../../../components/formsComponents";
import { CustomDatePicker } from "../../../components/formsComponents/CustomDatePicker";
import { StandardTableActions } from "../../../components/StandardTableActions/StandardTableActions";
import { DateFilterDropdown } from "../../../components/Table/components/DateFilterDropdown/DateFilterDropdown";
import { DataTable } from "../../../components/Table/DataTable";
import { useDateFilter } from "../../../components/Table/hooks/useDateFilter";
import {
  IDataTableProps,
  MobileLogoRenderType,
  RenderType,
} from "../../../components/Table/interfaces/IDataTableProps";
import {
  CSVExportPlugin,
  RefreshPlugin,
  SearchPlugin,
} from "../../../components/Table/plugins";
import { LoadState, MODULE_IDS } from "../../../constants/enums";
import { renderDropdownValue } from "../../../helpers";
import { useDispatchWrapper, useRecordCounts } from "../../../hooks";
import { Breadcrumb } from "../../../layout/fullLayout/breadcrumb/Breadcrumb";
import { IBreadcrumbProps } from "../../../layout/fullLayout/breadcrumb/IBreadcrumbProps";
import { IStoreState } from "../../../redux/initialStoreState";
import { IUser } from "../../../redux/UserProfileList/userProfile.types";
import {
  fetchUsersListAsync,
  upsertUserAysnc,
} from "../../../redux/UserProfileList/userProfileActions";
import { RoleBasedCustomButton } from "../../../security/RoleBasedComponents/RoleBasedCustomButton/RoleBasedCustomButton";
import { AddQuickUserForm } from "./AddQuickUserForm";
import { BeforeProfileTypes } from "./UsersList.types";
import { AddNewUserForm } from "./AddNewUserForm";
import { AddUserButton } from "./AddUserButton";
import { useBillingCompanyFromStorage } from "../../../providers";

export const BeforeProfile: React.FC = () => {
  const BCrumb: IBreadcrumbProps["items"] = [
    {
      to: "/dashbaord",
      title: "Dashboard",
    },
    {
      title: "users",
    },
  ];

  const { data, totalNumberOfRecords, loading } = useSelector(
    (storeState: IStoreState) => storeState.userprofile.users,
  );
  const [openCreateUser, setOpenCreateUser] = React.useState<
    "QUICK" | "FULL" | null
  >(null);

  const [tablePagination, setTablePagination] = React.useState({
    pageNumber: 1,
    rowsInPerPage: 50,
  });
  const [selectedOpp, setSelectedOppr] = React.useState<{
    isAllSelected: boolean;
    leads: BeforeProfileTypes[];
  }>({
    isAllSelected: false,
    leads: [],
  });
  const [status, setStatus] = React.useState<
    BeforeProfileTypes["status"] | "-1"
  >("ACTIVE");

  const { date, type, handleDateSelectChange, dateDropdownList, setDate } =
    useDateFilter({ defaultType: "allTimes" });

  const dispatch = useDispatchWrapper();
  const navigate = useNavigate();

  const [searchType, setSearchType] = React.useState<string[]>(["first_name"]);
  const [search, setSearch] = React.useState<string>("");
  const [saveLoading, setSaveLoading] = React.useState(false);
  const billingComponyUuid = useBillingCompanyFromStorage();

  // handle delete/ active an item from list
  const handleActiveInactiveItem = (data: IUser) => {
    dispatch(
      upsertUserAysnc(data, null, (isSuccess) => {
        if (isSuccess) {
          fetchList();
        }
        setSaveLoading(false);
      }),
    );
  };

  const fetchList = async () => {
    if (billingComponyUuid.customer_uuid) {
      dispatch(
        fetchUsersListAsync(
          {
            status: status,
            page: tablePagination.pageNumber,
            rowsPerPage: tablePagination.rowsInPerPage,
            columns: searchType,
            value: search,
            fromDate: date.fromDate,
            toDate: date.toDate,
          },
          billingComponyUuid.customer_uuid,
        ),
      );
    }
  };

  React.useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tablePagination, date, search, searchType, status]);

  const menuItems =
    selectedOpp.leads.length > 0 || selectedOpp.isAllSelected
      ? [
          {
            key: "action",
            onRender: () => (
              <CustomSelect
                sx={{ minWidth: "120px" }}
                value={""}
                displayEmpty
                renderValue={renderDropdownValue(`Choose Bulk Action`)}
                //onChange={(e) => setBranchOffice(e.target.value as string)}
              >
                <MenuItem value="1">Email</MenuItem>
              </CustomSelect>
            ),
          },
        ]
      : [];

  const usersTableProps: IDataTableProps = {
    isPagination: true,
    totalRecords: totalNumberOfRecords,
    rowsPerPageOptions: tablePagination.rowsInPerPage,
    isDataLoading: loading === LoadState.InProgress,
    // selectionMode: "single",
    uniqueRowKeyName: "id",
    tableCommandBarProps: {
      leftItems: {
        customPlugins: [
          ...menuItems,
          {
            key: "status",
            onRender: () => (
              <CustomSelect
                sx={{ minWidth: "120px" }}
                value={status}
                onChange={(e) => setStatus(e.target.value as "ACTIVE")}
              >
                <MenuItem value="-1">All Status</MenuItem>
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
              </CustomSelect>
            ),
          },
          {
            key: "byDate",
            onRender: () => (
              <DateFilterDropdown
                value={type}
                options={dateDropdownList}
                onChange={handleDateSelectChange}
              />
            ),
          },
          {
            key: "fromDate",
            onRender: () =>
              type === "custom" ? (
                <CustomDatePicker
                  sx={{ minWidth: "150px" }}
                  value={date.fromDate}
                  onChange={(newValue) => {
                    const newDate = moment(newValue).format("YYYY-MM-DD");
                    setDate({ ...date, fromDate: newDate });
                  }}
                />
              ) : (
                <></>
              ),
          },
          {
            key: "toDate",
            onRender: () =>
              type === "custom" ? (
                <CustomDatePicker
                  sx={{ minWidth: "150px" }}
                  value={date.toDate}
                  onChange={(newValue) => {
                    const newDate = moment(newValue).format("YYYY-MM-DD");
                    setDate({ ...date, toDate: newDate });
                  }}
                />
              ) : (
                <></>
              ),
          },
          {
            key: "csvExport",
            onRender: (columns, items) => (
              <CSVExportPlugin
                columns={columns}
                items={items}
                filePrefixName="Report"
              />
            ),
          },
          {
            key: "refresh",
            onRender: () => <RefreshPlugin onClick={fetchList} />,
          },
        ],
      },
      rightItems: {
        plugins: {
          // searchField: {
          //   searchKeys: [],
          //   items: data,
          // },
        },
        customPlugins: [
          {
            key: "search",
            onRender: () => {
              return (
                <SearchPlugin
                  selectedDropdownValue={searchType}
                  dropdownOptions={[
                    { label: "Name", value: "first_name" },
                    { label: "Email", value: "personal_email" },
                    { label: "Mobile", value: "mobile" },
                    { label: "Department", value: "department" },
                    { label: "Branch", value: "branch" },
                  ]}
                  onDropdownChange={(value) => setSearchType(value)}
                  onChange={(newValue) => {
                    setSearch(newValue);
                  }}
                />
              );
            },
          },
        ],
      },
    },
    mobileLogo: {
      type: MobileLogoRenderType.reactNode,
      onMobileLogoRender: (data: any) => {
        return (
          <Avatar
            sx={{
              width: 45,
              height: 45,
              color: "#fff",
              ml: "-8px",
              mr: 2,
            }}
          ></Avatar>
        );
      },
    },
    columns: [
      {
        key: "view",
        headerName: "Actions",
        fieldName: "",
        renderType: RenderType.CUSTOM_RENDER,
        headingAlign: "center",
        onRowCellRender: (value, row) => {
          return (
            <StandardTableActions
              status={row.status}
              onDeleteClick={() => {
                handleActiveInactiveItem({ ...row, status: "INACTIVE" });
              }}
              onReactiveClick={() => {
                handleActiveInactiveItem({ ...row, status: "ACTIVE" });
              }}
              onEditClick={() => {
                navigate("/users/manage/" + row.user_uuid);
              }}
            />
          );
        },
      },
      {
        key: "status",
        headerName: "Status",
        fieldName: "status",
        enableSorting: true,
        renderType: RenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IUser) => {
          return <StatusRenderer status={row.status} />;
        },
      },
      {
        key: "full_name",
        headerName: "Employee Name",
        fieldName: "full_name",
        exportCellWidth: 25,
        renderType: RenderType.CUSTOM_RENDER,
        onRowCellRender: (value, row: IUser) => {
          return (
            <Stack direction="row" alignItems={"center"} spacing={1}>
              {value.value && (
                <>
                  <Avatar
                    sx={{
                      bgcolor:
                        value.column.rowIndex % 2 === 0
                          ? deepOrange[400]
                          : deepPurple[400],
                    }}
                  >
                    {value.value.substring(0, 1)}
                  </Avatar>
                  <CustomLink
                    to={"/users/manage/" + row.user_uuid}
                    // /users/manage/" + row.user_fact_id
                    label={row.full_name || ""}
                  />
                </>
              )}
            </Stack>
          );
        },
      },
      {
        key: "role_name",
        headerName: "Role",
        fieldName: "role_name",
        renderType: RenderType.TEXT,
        exportCellWidth: 30,
        enableSorting: true,
      },
      {
        key: "email",
        headerName: "Email",
        fieldName: "email",
        renderType: RenderType.TEXT_DARK,
        exportCellWidth: 30,
        enableSorting: true,
      },
      {
        key: "mobile",
        headerName: "Mobile",
        fieldName: "mobile",
        exportCellWidth: 25,
        renderType: RenderType.TEXT,
      },
      {
        key: "billing_company_name",
        headerName: "Company",
        fieldName: "billing_company_name",
        exportCellWidth: 25,
        renderType: RenderType.TEXT,
      },
      {
        key: "department_name",
        headerName: "Department",
        fieldName: "department_name",
        renderType: RenderType.TEXT,
        exportCellWidth: 30,
        enableSorting: true,
      },
      {
        key: "branch_name",
        headerName: "Branch",
        fieldName: "branch_name",
        renderType: RenderType.TEXT,
        exportCellWidth: 30,
        enableSorting: true,
      },
    ],
    items: data,
    onPageChange: (newPageNumber: number) => {
      setTablePagination({ ...tablePagination, pageNumber: newPageNumber });
    },
    onRowsPerPageChange: (pageNumber: number, rowsPerPage: number) => {
      setTablePagination({
        pageNumber: pageNumber,
        rowsInPerPage: rowsPerPage,
      });
    },
    onSelection: (isAllSelected: boolean, rows: BeforeProfileTypes[]) => {
      setSelectedOppr({ isAllSelected: isAllSelected, leads: rows });
    },
  };

  const handleToggleCreate = () => {
    setOpenCreateUser(null);
    // navigate("/users/manage");
  };

  const { list: recordsCounts, loading: recordCountsLoading } =
    useRecordCounts();

  return (
    <PageContainer title="View Employees" description="this is before profile">
      <Breadcrumb title="" items={BCrumb} />

      {/* <IconicMultiColumnCard
        columns={recordsCounts.map((item) => {
          return {
            heading: capitalizeWords(item.status),
            value: item.count,
            colSize: 2,
          };
        })}
      /> */}

      <StandardCard>
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          sx={{
            mx: 5,
            my: 2,
          }}
        >
          <Typography variant="h2" fontWeight={"700"}>
            Employees
          </Typography>
          <AddUserButton onClick={(val) => setOpenCreateUser(val)} />
        </Box>

        <DataTable {...usersTableProps} />
      </StandardCard>
      {/* {openCreateUser && <Navigate to="/users/manage" replace={true} />} */}

      {openCreateUser === "QUICK" && (
        <AddQuickUserForm open={true} onClose={handleToggleCreate} />
      )}

      {openCreateUser === "FULL" && (
        <AddNewUserForm open={true} onClose={handleToggleCreate} />
      )}

      {saveLoading && <LoadingDialog open={saveLoading} />}
    </PageContainer>
  );
};
